import { useSearchParams, useParams } from 'react-router-dom';
import { Building2 } from 'lucide-react';
import { Breadcrumbs, Button, ButtonLink, Card, CardBody, CardHeader, DescriptionList, DemoBadge, ErrorState, LoadingState } from '@/components/ui';
import { CountryDisplay } from '@/components/location';
import { getBusiness, getBusinessDetails, useQuery } from '@/data';
import { LiveBusinessDetailsPanels } from '@/features/pharmacies/details/LiveBusinessDetailsPanels';
import {
  BUSINESS_WORKSPACE_TABS,
  BusinessWorkspaceNav,
  type BusinessWorkspaceTab,
} from '@/features/pharmacies/details/BusinessWorkspaceNav';
import { BusinessStatusBadge } from '@/features/pharmacies/components/StatusBadges';
import { ProductionLinkBadge } from '@/features/production-linking/ProductionBadges';
import { ApiError } from '@/lib/api';
import { formatDate, formatDateTime } from '@/lib/format';

/** Keep in sync with BusinessWorkspaceNav — unknown ?tab= values fall back to overview. */
const TAB_IDS = new Set<string>(BUSINESS_WORKSPACE_TABS.map((t) => t.id));

/** Category-agnostic business details (e.g. Al Rahhal Supermarket). */
export function BusinessDetailsPage({ listPath = '/supermarkets', listLabel = 'Supermarkets' }: { listPath?: string; listLabel?: string }) {
  const { id = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const tabParam = params.get('tab') ?? 'overview';
  const tab = (TAB_IDS.has(tabParam) ? tabParam : 'overview') as BusinessWorkspaceTab;
  const setTab = (next: BusinessWorkspaceTab) => {
    setParams(
      (prev) => {
        const sp = new URLSearchParams(prev);
        if (next === 'overview') sp.delete('tab');
        else sp.set('tab', next);
        return sp;
      },
      { replace: true },
    );
  };

  const { data: business, error, loading, reload } = useQuery(() => getBusiness(id), [id]);
  const { data: details, loading: detailsLoading, reload: reloadDetails } = useQuery(() => getBusinessDetails(id), [id]);

  if (loading) return <LoadingState label="Loading business…" />;
  if (error || !business) {
    const notFound = error instanceof ApiError && error.status === 404;
    return (
      <div className="card">
        <ErrorState
          title={notFound ? 'Business not found' : 'Could not load business'}
          message={notFound ? 'This business does not exist or was removed.' : (error?.message ?? 'Unknown error')}
          action={
            <div className="flex gap-2">
              {!notFound && (
                <Button variant="secondary" onClick={reload}>
                  Try again
                </Button>
              )}
              <ButtonLink to={listPath}>Back to {listLabel}</ButtonLink>
            </div>
          }
        />
      </div>
    );
  }

  return (
    <>
      <Breadcrumbs items={[{ label: listLabel, to: listPath }, { label: business.name }]} />

      <div className="card mb-6 p-5 sm:p-6">
        <div className="flex min-w-0 items-start gap-4">
          <span className="hidden h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary sm:flex">
            <Building2 size={24} aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <h1 className="flex flex-wrap items-center gap-2 text-[22px] font-semibold tracking-tight text-ink">
              {business.name}
              {business.isDemo && <DemoBadge />}
            </h1>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-ink-secondary">
              <CountryDisplay country={business.country} city={business.city} flagSize={16} />
              <span className="font-mono text-ink-muted">{business.code}</span>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <BusinessStatusBadge status={business.status} />
              {details && <ProductionLinkBadge status={details.overview.productionLinkStatus} />}
            </div>
          </div>
        </div>
      </div>

      <BusinessWorkspaceNav active={tab} onChange={setTab} />

      <div className="grid gap-6 xl:grid-cols-2">
        {tab === 'overview' && (
          <Card>
            <CardHeader title="Business Information" icon={<Building2 size={16} aria-hidden="true" />} />
            <CardBody>
              <DescriptionList
                items={[
                  { label: 'Business Name', value: business.name },
                  { label: 'Customer Code', value: <span className="font-mono">{business.code}</span> },
                  { label: 'Category', value: business.category },
                  { label: 'Location', value: <CountryDisplay country={business.country} city={business.city} /> },
                  ...(business.administrativeAreaLevel1
                    ? [{ label: 'Division', value: business.administrativeAreaLevel1 }]
                    : []),
                  ...(business.area ? [{ label: 'Area', value: business.area }] : []),
                  ...(business.address ? [{ label: 'Address', value: business.address }] : []),
                  { label: 'Contact Person', value: business.contactPerson },
                  { label: 'Phone', value: <span className="tabular-nums">{business.phone}</span> },
                  { label: 'Email', value: business.email ?? '—' },
                  { label: 'Customer Since', value: formatDate(business.createdAt) },
                  { label: 'Last Updated', value: formatDateTime(business.updatedAt) },
                  ...(business.notes ? [{ label: 'Notes', value: <span className="whitespace-pre-line">{business.notes}</span> }] : []),
                ]}
              />
              <details className="mt-4">
                <summary className="cursor-pointer text-xs font-medium text-ink-secondary hover:text-ink">Technical details</summary>
                <p className="mt-2 font-mono text-xs text-ink-muted break-all">Business ID: {business.id}</p>
              </details>
            </CardBody>
          </Card>
        )}

        <LiveBusinessDetailsPanels
          details={details ?? null}
          loading={detailsLoading}
          businessId={business.id}
          onRefresh={() => void reloadDetails()}
          tab={tab}
          onRequestTab={setTab}
        />
      </div>
    </>
  );
}
